"use client";

import { useEmployerPermission } from "@/components/contexts/EmployerPermissionContext";
import { MedicalCheckupIcon } from "@/components/icons/MedicalCheckupIcon";
import { getEmployersZendeskUrl } from "@/constants/zendesk-urls/employers-zendesk-urls";
import { isFeatureFlagStressCheckEnabled } from "@/env/config";
import { cn } from "@/lib/utils";
import type {
  Subject,
  SubjectActions,
} from "@/server/models/employer-office/employer-ability";
import { employersZendeskSsoLink } from "@/server/models/zendesk/zendesk-sso-link";
import { pagesPath } from "@/utils/pages-path";
import OfficeIcon from "@public/icons/company-icon.svg";
import DashboardIcon from "@public/icons/dashboard-icon.svg";
import FeedRectangleIcon from "@public/icons/feed-rectangle-icon.svg";
import HelpIcon from "@public/icons/help-icon.svg";
import HomeIcon from "@public/icons/home-icon.svg";
import MembersIcon from "@public/icons/members-icon.svg";
import MenuIcon from "@public/icons/menu-icon.svg";
import MyNumberCardIcon from "@public/icons/my-number-card-icon.svg";
import OffersIcon from "@public/icons/offers-icon.svg";
import Link from "next/link";
import { usePathname } from "next/navigation";
import type { ComponentType } from "react";
import { MdOutlinePsychology } from "react-icons/md";

// Material のアイコンは viewBox に余白があるため、他のサイドメニューアイコン
// （16〜18px の SVG）と見た目の大きさを揃えるには一回り大きく指定する
const StressCheckIcon = ({ className }: { className?: string }) => (
  <MdOutlinePsychology className={cn("size-6", className)} />
);

const navMenuItems: Record<
  string,
  {
    title: string;
    href: (officeId: string) => string;
    navigationPath?: string;
    Icon: ComponentType<{ className?: string }>;
    isExternal?: true;
    // feature flag などによる表示可否の判定
    // 未指定の場合は常に表示される
    isVisible?: () => boolean;
    // サイドメニュー項目の表示に必要な権限
    // 複数ある場合はどれか一つを満たせば表示される（OR相当）
    requiredPermissions?: Array<{
      subject: Subject;
      action: SubjectActions<Subject>;
    }>;
  }
> = {
  home: {
    title: "ホーム",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).$url().path,
    Icon: HomeIcon,
  },
  dashboard: {
    title: "ダッシュボード",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).dashboard.$url().path,
    navigationPath: "dashboard",
    Icon: DashboardIcon,
    requiredPermissions: [
      {
        subject: "Dashboard",
        action: "read",
      },
    ],
  },
  notification: {
    title: "お知らせ",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).news.$url().path,
    navigationPath: "news",
    Icon: FeedRectangleIcon,
  },
  request: {
    title: "各種届出",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).applications.$url().path,
    navigationPath: "applications",
    Icon: MenuIcon,
    requiredPermissions: [
      {
        subject: "Application",
        action: "read",
      },
    ],
  },
  offers: {
    title: "特典サービス",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).offers.$url().path,
    navigationPath: "offers",
    Icon: OffersIcon,
    requiredPermissions: [
      {
        subject: "Offer",
        action: "read",
      },
    ],
  },
  healthCheck: {
    title: "健康診断",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).medical_checkups.$url().path,
    navigationPath: "medical-checkups",
    Icon: MedicalCheckupIcon,
    // 健診の補助金通知書だけを閲覧できる事業所ユーザーも、ここから補助金通知書一覧へ進む
    requiredPermissions: [
      {
        subject: "MedicalCheckup",
        action: "read",
      },
      {
        subject: "MedicalCheckupSubsidyNotice",
        action: "read",
      },
    ],
  },
  stressCheck: {
    title: "ストレスチェック",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).stress_check.$url().path,
    navigationPath: "stress-check",
    Icon: StressCheckIcon,
    isVisible: isFeatureFlagStressCheckEnabled,
    requiredPermissions: [
      {
        subject: "StressCheck",
        action: "read",
      },
    ],
  },
  officeMembers: {
    title: "被保険者",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).office_members.$url().path,
    navigationPath: "office-members",
    Icon: MembersIcon,
    requiredPermissions: [
      {
        subject: "Member",
        action: "read",
      },
    ],
  },
  myNumber: {
    title: "マイナ保険証",
    href: () => employersZendeskSsoLink(getEmployersZendeskUrl("マイナ保険証")),
    isExternal: true,
    Icon: MyNumberCardIcon,
  },
  office: {
    title: "事業所情報",
    href: (officeId: string) =>
      pagesPath.employers._officeId(officeId).office.$url().path,
    Icon: OfficeIcon,
    navigationPath: "office",
    requiredPermissions: [
      {
        subject: "Office",
        action: "read",
      },
      {
        subject: "Employer",
        action: "read",
      },
      {
        subject: "Permission",
        action: "read",
      },
    ],
  },
  help: {
    title: "ヘルプ",
    href: () => employersZendeskSsoLink(getEmployersZendeskUrl("ヘルプ")),
    isExternal: true,
    Icon: HelpIcon,
  },
};

const NavMenuItem = ({
  title,
  href,
  Icon,
  active,
  isExternal,
}: {
  title: string;
  href: string;
  Icon: ComponentType<{ className?: string }>;
  active: boolean;
  isExternal: boolean;
}) => {
  return (
    <Link
      href={href}
      target={isExternal ? "_blank" : undefined}
      className={cn(
        "group",
        "flex h-12 w-full flex-row items-center gap-2 pl-6 transition",
        "hover:bg-primary-1",
        active && "bg-primary-1 text-primary-7",
      )}
      prefetch={!isExternal}
    >
      <span className="inline-flex w-6 items-center justify-center">
        <Icon
          className={cn(
            active
              ? "fill-primary-7 text-primary-7"
              : "fill-monotone-7 text-monotone-7",
            "group-hover:fill-primary-7 group-hover:text-primary-7",
          )}
        />
      </span>
      <p
        className={cn(
          "font-bold group-hover:text-primary-7",
          active && "text-primary-7",
        )}
      >
        {title}
      </p>
    </Link>
  );
};

const useActiveTab = (): string | null => {
  const pathname = usePathname();
  const navPath = pathname.split("/")[2];
  if (typeof navPath === "undefined") {
    return "home";
  } else {
    for (const [key, navItem] of Object.entries(navMenuItems)) {
      if ("navigationPath" in navItem && navItem.navigationPath === navPath) {
        return key;
      }
    }
  }

  return null;
};

export const EmployerSideMenuNav = ({
  params,
}: {
  params: { officeId: string };
}) => {
  const activeTab = useActiveTab();
  const { checkEmployerPermission } = useEmployerPermission();

  return (
    <nav className="flex flex-col">
      {Object.entries(navMenuItems)
        .filter(([, navItem]) => {
          if (navItem.isVisible != null && !navItem.isVisible()) {
            return false;
          }
          if (navItem.requiredPermissions) {
            // どれか一つでも権限を満たしていれば表示
            return navItem.requiredPermissions.some(({ subject, action }) =>
              checkEmployerPermission(subject, action),
            );
          }
          return true;
        })
        .map(([key, navItem]) => (
          <NavMenuItem
            key={key}
            title={navItem.title}
            href={navItem.href(params.officeId)}
            Icon={navItem.Icon}
            active={key === activeTab}
            isExternal={!!navItem.isExternal}
          />
        ))}
    </nav>
  );
};
